import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, User, Calendar, Eye, EyeOff, Check, X, Gift } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [dob, setDob] = useState('');
  const [state, setState] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Pick referral code from ?ref=CODE
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = (params.get('ref') || '').trim().toUpperCase();
    if (code) {
      setReferralCode(code);
      axios.get(`${API_URL}/api/referral/lookup/${code}`)
        .then((res) => setReferralInfo(res.data))
        .catch(() => setReferralInfo(null));
    }
  }, [location.search]);

  // Max date = 15 years ago
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 15);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!acceptedTerms) {
      setError('You must accept the Terms & Conditions to register');
      return;
    }

    if (!dob) {
      setError('Please enter your date of birth');
      return;
    }

    // Client-side age check
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (age < 15) {
      setError('You must be at least 15 years old to register');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    const result = await register(name, email, password, dob, acceptedTerms, state, referralCode);
    setLoading(false);

    if (result.success) {
      // Email verification temporarily disabled — go straight to dashboard.
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%)' }}>
      <div className="w-full max-w-md">
        <div className="glass-card p-8 shadow-xl">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-6 animate-fade-in">
              <img 
                src="/aptivon-logo-v2.png" 
                alt="Aptivon Logo" 
                className="h-20 w-auto drop-shadow-2xl"
              />
            </div>
            <p className="text-[#6B7280] text-lg">Create your account and get 100 free points!</p>
          </div>

          {referralInfo?.valid && (
            <div className="mb-5 p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 flex items-start gap-3" data-testid="referral-banner">
              <Gift className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold text-amber-800">
                  You're joining {referralInfo.referrer_name}'s circle
                </p>
                <p className="text-amber-700">
                  Sign up now and get an extra <strong>+{referralInfo.bonus_points} bonus points</strong>. Referral code <code className="px-1.5 py-0.5 rounded bg-amber-200 font-mono font-bold">{referralCode}</code> will be auto-applied.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-bold text-[#111827] mb-2">Name</label>
              <div className="relative">
                <User className="absolute left-3 top-3 w-5 h-5 text-[#6B7280]" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors"
                  placeholder="Your Name"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#111827] mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-[#6B7280]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors"
                  placeholder="your@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#111827] mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-[#6B7280]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors"
                  placeholder="Min. 6 characters"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-[#6B7280] hover:text-[#0EA5E9] transition-colors"
                  data-testid="toggle-password-visibility"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {password && (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs">
                  {password.length >= 6 ? (
                    <><Check className="w-3.5 h-3.5 text-emerald-500" /><span className="text-emerald-600">Strong enough</span></>
                  ) : (
                    <><X className="w-3.5 h-3.5 text-amber-500" /><span className="text-amber-600">{6 - password.length} more character{6 - password.length === 1 ? '' : 's'} needed</span></>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-[#111827] mb-2">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-[#6B7280]" />
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full pl-10 pr-12 py-3 border-2 rounded-xl focus:outline-none transition-colors ${
                    confirmPassword && password !== confirmPassword ? 'border-rose-300 focus:border-rose-500' : 'border-[#E5E7EB] focus:border-[#0EA5E9]'
                  }`}
                  placeholder="Re-type your password"
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-3 text-[#6B7280] hover:text-[#0EA5E9] transition-colors"
                  data-testid="toggle-confirm-visibility"
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs">
                  {password === confirmPassword ? (
                    <><Check className="w-3.5 h-3.5 text-emerald-500" /><span className="text-emerald-600">Passwords match</span></>
                  ) : (
                    <><X className="w-3.5 h-3.5 text-rose-500" /><span className="text-rose-600">Passwords don't match</span></>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-[#111827] mb-2">Date of Birth</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 w-5 h-5 text-[#6B7280]" />
                <input
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  max={maxDateStr}
                  className="w-full pl-10 pr-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors"
                  required
                  data-testid="dob-input"
                />
              </div>
              <p className="text-xs text-[#9CA3AF] mt-1">You must be at least 15 years old</p>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#111827] mb-2">State</label>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors text-[#111827]"
                required
                data-testid="state-input"
              >
                <option value="">Select your state</option>
                {["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal"].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {["Andhra Pradesh","Telangana","Assam","Odisha","Sikkim","Nagaland"].includes(state) && (
                <p className="text-xs text-amber-600 mt-1 font-medium">Paid quiz pools are not available in your state. Practice mode is accessible.</p>
              )}
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms-accept"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-[#E5E7EB] text-[#0EA5E9] focus:ring-[#0EA5E9]"
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms-accept" className="text-sm text-[#6B7280]">
                I agree to the{' '}
                <Link to="/terms" className="text-[#0EA5E9] font-bold hover:underline" target="_blank">Terms & Conditions</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-[#0EA5E9] font-bold hover:underline" target="_blank">Privacy Policy</Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-[#0EA5E9] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-button"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#E5E7EB]"></div>
            <span className="text-xs text-[#9CA3AF] font-medium">OR</span>
            <div className="flex-1 h-px bg-[#E5E7EB]"></div>
          </div>

          {/* Google Sign Up */}
          <div className="mt-4">
            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white border-2 border-[#E5E7EB] rounded-2xl font-bold text-[#111827] hover:bg-slate-50 transition-colors"
              data-testid="google-register-button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign up with Google
            </button>
            <p className="text-xs text-[#9CA3AF] text-center mt-2">
              By signing up with Google, you agree to our{' '}
              <Link to="/terms" className="text-[#0EA5E9] hover:underline" target="_blank">T&C</Link>{' & '}
              <Link to="/privacy" className="text-[#0EA5E9] hover:underline" target="_blank">Privacy Policy</Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <p className="text-sm text-[#6B7280]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#0EA5E9] font-bold hover:underline" data-testid="login-link">
                Login here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
